import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowLeft,
  Check,
  Plus,
  ArrowRight,
  Sparkles,
  Package,
  Target,
  HelpCircle,
  Shield,
  Truck,
  Clock,
  Phone,
  Loader2,
  X,
} from "lucide-react";
import { useInquiry } from "@/lib/inquiry-store";
import { ProductCard } from "@/components/site/ProductCard";
import { toast } from "sonner";
import { useProduct, useCategoryLabel, useProducts } from "@/lib/use-catalog";
import { resolveImage } from "@/lib/product-images";

import { getProductBySlug } from "@/lib/catalog.functions";
import { productJsonLd, faqJsonLd, breadcrumbJsonLd } from "@/lib/seo.schemas";

const SITE_ORIGIN = "https://eventsverige.se";

const CATEGORY_LABELS: Record<string, string> = {
  hoppborgar: "Hoppborgar",
  tivolispel: "Tivolispel",
  "scen-ljud-ljus": "Scen, ljud & ljus",
};

function toAbsoluteUrl(src: string): string {
  if (!src) return src;
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  if (src.startsWith("/")) return `${SITE_ORIGIN}${src}`;
  return `${SITE_ORIGIN}/${src}`;
}

function categoryHrefFor(slug: string | undefined): string {
  if (slug === "hoppborgar") return "/hoppborgar";
  if (slug === "tivolispel") return "/tivolispel";
  if (slug === "scen-ljud-ljus") return "/scen-ljud-ljus";
  return `/kategori/${slug ?? ""}`;
}

export const Route = createFileRoute("/produkter/$slug")({
  loader: ({ params }) => getProductBySlug({ data: { slug: params.slug } }),
  head: ({ loaderData, params }) => {
    const p = loaderData;
    const title =
      p?.meta_title ||
      (p?.name ? `${p.name} — hyra hos Event Sverige` : `${params.slug} — Event Sverige`);
    const desc =
      p?.meta_description ||
      p?.short ||
      `Hyr ${p?.name ?? params.slug} hos Event Sverige — perfekt för företag, kommun och skola.`;
    const kw = p?.synonyms?.length ? [p.name, ...p.synonyms].join(", ") : undefined;
    const url = `${SITE_ORIGIN}/produkter/${params.slug}`;

    const mainImg = p?.image ? toAbsoluteUrl(resolveImage(p.image)) : null;
    const gallery: string[] = Array.isArray(p?.gallery)
      ? p.gallery.map((g: string) => toAbsoluteUrl(g))
      : [];
    const allImages = [mainImg, ...gallery].filter((x): x is string => !!x && x.startsWith("http"));
    const ogImg = mainImg && mainImg.startsWith("http") ? mainImg : null;

    const categoryLabel = p?.category_slug
      ? (CATEGORY_LABELS[p.category_slug] ?? p.category_slug)
      : undefined;

    const scripts: { type: string; children: string }[] = [];
    if (p?.name) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(
          productJsonLd({
            name: p.name,
            description: p.description || p.short,
            image: allImages.length > 1 ? allImages : (allImages[0] ?? undefined),
            url,
            sku: p.slug,
            category: categoryLabel,
          }),
        ),
      });
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { name: "Hem", url: `${SITE_ORIGIN}/` },
            { name: "Produkter", url: `${SITE_ORIGIN}/produkter` },
            ...(categoryLabel
              ? [{ name: categoryLabel, url: `${SITE_ORIGIN}${categoryHrefFor(p.category_slug)}` }]
              : []),
            { name: p.name, url },
          ]),
        ),
      });
    }
    if (p?.faq?.length) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd(p.faq)),
      });
    }
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        ...(kw ? [{ name: "keywords", content: kw }] : []),
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: `/produkter/${params.slug}` },
        { property: "og:type", content: "product" },
        ...(ogImg
          ? [
              { property: "og:image", content: ogImg },
              { name: "twitter:image", content: ogImg },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: ProductPage,
});

function ProductPage() {
  const { slug } = Route.useParams();
  const { data: product, isLoading } = useProduct(slug);
  const { data: allProducts } = useProducts();
  const categoryLabel = useCategoryLabel(product?.category_slug);
  const { add, has } = useInquiry();
  const [lightbox, setLightbox] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Produkten finns inte</h1>
        <Link
          to="/produkter"
          className="mt-6 inline-flex items-center gap-2 text-accent-foreground bg-accent rounded-full px-5 py-2.5 font-semibold"
        >
          <ArrowLeft className="h-4 w-4" /> Tillbaka till sortiment
        </Link>
      </div>
    );
  }

  const imgSrc = resolveImage(product.image);
  const added = has(product.slug);
  const related = (allProducts ?? [])
    .filter((p) => p.category_slug === product.category_slug && p.slug !== product.slug)
    .slice(0, 3);

  const categoryHref =
    product.category_slug === "hoppborgar"
      ? "/hoppborgar"
      : product.category_slug === "tivolispel"
        ? "/tivolispel"
        : product.category_slug === "scen-ljud-ljus"
          ? "/scen-ljud-ljus"
          : `/kategori/${product.category_slug}`;

  const handleAdd = () => {
    if (added) return;
    add({ slug: product.slug, name: product.name, image: imgSrc });
    toast.success(`${product.name} tillagd i din förfrågan`);
  };

  return (
    <div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8 pb-2">
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            Hem
          </Link>
          <span>/</span>
          <Link to="/produkter" className="hover:text-foreground">
            Produkter
          </Link>
          <span>/</span>
          <Link to={categoryHref} className="hover:text-foreground">
            {categoryLabel}
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium truncate">{product.name}</span>
        </nav>
      </div>

      <div className="experience-wrap quote-panel">
        <h2>Vill ni ha ett färdigt upplägg?</h2>
        <p>Se hur spelen kan bli ett sammanhållet event med värd och stämningsljus.</p>
        <Link to="/koncept" className="text-link">
          Utforska våra koncept <ArrowRight size={16} />
        </Link>
      </div>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4 pb-16 grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-3">
          <div className="aspect-square lg:aspect-[4/3] overflow-hidden rounded-3xl bg-card border border-border relative">
            <img
              src={imgSrc}
              alt={product.name}
              width={1280}
              height={960}
              className="h-full w-full object-cover"
            />
            {product.popular && (
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-3 py-1.5 text-xs font-bold uppercase tracking-wider shadow-lg">
                <Sparkles className="h-3.5 w-3.5" /> Populär
              </span>
            )}
            {product.brandable && (
              <span
                className="hidden sm:flex absolute top-1/2 -translate-y-1/2 right-0 translate-x-1/2 items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-xs font-bold uppercase tracking-widest shadow-xl rounded-md rotate-90 origin-center"
                aria-label="Går att branda"
              >
                <Sparkles className="h-3.5 w-3.5" /> Går att branda
              </span>
            )}
            {product.brandable && (
              <span className="sm:hidden absolute bottom-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow-lg">
                <Sparkles className="h-3.5 w-3.5" /> Går att branda
              </span>
            )}
          </div>
          {product.video_url && (
            <video
              src={product.video_url}
              controls
              className="mt-4 w-full rounded-2xl bg-black aspect-video"
            />
          )}
          {product.gallery && product.gallery.length > 0 && (
            <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-2">
              {product.gallery.map((g, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightbox(g)}
                  className="aspect-square overflow-hidden rounded-lg bg-card border border-border hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <img src={g} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 lg:sticky lg:top-24">
          <span className="text-xs font-bold uppercase tracking-widest text-accent">
            {categoryLabel}
          </span>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-extrabold leading-tight">
            {product.name}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">{product.description}</p>

          <dl className="mt-8 grid grid-cols-2 gap-3">
            {[
              ...(product.dimensions ? [{ label: "Mått", value: product.dimensions }] : []),
              ...(product.power ? [{ label: "Elbehov", value: product.power }] : []),
              ...(product.weight ? [{ label: "Hantering", value: "Stäms av inför leverans" }] : []),
              ...product.specs,
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-border bg-card p-4">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {s.label}
                </dt>
                <dd className="mt-1 font-display font-bold text-base leading-tight">{s.value}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-6 rounded-2xl bg-accent/10 border border-accent/30 p-5">
            <p className="text-sm">
              <strong>Pris enligt offert.</strong> Vi skräddarsyr varje pris efter eventets längd,
              leveransort och paket. Bygg din önskelista så återkommer vi — eller hör av dig direkt
              på 031-797 55 99.
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={handleAdd}
              disabled={added}
              className={`flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base font-bold transition ${
                added
                  ? "bg-primary text-primary-foreground"
                  : "bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg shadow-accent/20"
              }`}
            >
              {added ? (
                <>
                  <Check className="h-5 w-5" /> Tillagd
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" /> Lägg i förfrågan
                </>
              )}
            </button>
            <Link
              to="/forfragan"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-base font-semibold hover:bg-secondary"
            >
              Min förfrågan <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl bg-secondary/50 p-3">
              <Shield className="h-5 w-5 mx-auto text-accent mb-1" />
              <p className="text-[11px] font-semibold">Försäkrad</p>
            </div>
            <div className="rounded-xl bg-secondary/50 p-3">
              <Truck className="h-5 w-5 mx-auto text-accent mb-1" />
              <p className="text-[11px] font-semibold">Rigg som tillval</p>
            </div>
            <div className="rounded-xl bg-secondary/50 p-3">
              <Clock className="h-5 w-5 mx-auto text-accent mb-1" />
              <p className="text-[11px] font-semibold">Svar 24 h</p>
            </div>
          </div>
        </div>
      </section>

      {(product.long_description ||
        product.features.length > 0 ||
        product.includes.length > 0 ||
        product.best_for.length > 0) && (
        <section className="bg-secondary/40 border-y border-border">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid gap-12 lg:grid-cols-3">
              {product.long_description && (
                <div className="lg:col-span-2">
                  <h2 className="font-display text-3xl font-extrabold mb-4">
                    Om {product.name.toLowerCase()}
                  </h2>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {product.long_description}
                  </p>

                  {product.features.length > 0 && (
                    <div className="mt-10">
                      <h3 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-accent" /> Det här ingår i upplevelsen
                      </h3>
                      <ul className="grid sm:grid-cols-2 gap-3">
                        {product.features.map((f) => (
                          <li
                            key={f}
                            className="flex items-start gap-3 rounded-xl bg-card border border-border p-4"
                          >
                            <Check className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                            <span className="text-sm">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <aside className="space-y-6">
                {product.includes.length > 0 && (
                  <div className="rounded-2xl bg-card border border-border p-6">
                    <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
                      <Package className="h-5 w-5 text-accent" /> I leveransen
                    </h3>
                    <ul className="space-y-2">
                      {product.includes.map((i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                          <span>{i}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {product.best_for.length > 0 && (
                  <div className="rounded-2xl bg-primary text-primary-foreground p-6">
                    <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
                      <Target className="h-5 w-5 text-accent" /> Passar bra till
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {product.best_for.map((b) => (
                        <span
                          key={b}
                          className="rounded-full bg-primary-foreground/10 px-3 py-1.5 text-xs font-semibold"
                        >
                          {b}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          </div>
        </section>
      )}

      {product.faq.length > 0 && (
        <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-3xl font-extrabold mb-8 flex items-center gap-2">
            <HelpCircle className="h-7 w-7 text-accent" /> Vanliga frågor
          </h2>
          <div className="space-y-3">
            {product.faq.map((item, i) => (
              <details
                key={i}
                className="group rounded-2xl bg-card border border-border p-5 open:shadow-md transition"
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-semibold">
                  <span>{item.q}</span>
                  <Plus className="h-5 w-5 text-accent shrink-0 transition-transform group-open:rotate-45" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold">
              Lägg till <span className="text-accent">{product.name}</span> i din förfrågan
            </h2>
            <p className="mt-3 text-primary-foreground/80">
              Det är kostnadsfritt och oförbindande. Vi granskar förfrågan och återkommer med
              offert.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <button
              onClick={handleAdd}
              disabled={added}
              className={`inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-base font-bold transition ${
                added
                  ? "bg-primary-foreground/10 text-primary-foreground"
                  : "bg-accent text-accent-foreground hover:bg-accent/90"
              }`}
            >
              {added ? (
                <>
                  <Check className="h-5 w-5" /> Tillagd
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" /> Lägg till
                </>
              )}
            </button>
            <a
              href="tel:+46317975599"
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/30 px-6 py-3.5 text-base font-semibold hover:bg-primary-foreground/10"
            >
              <Phone className="h-4 w-4" /> 031-797 55 99
            </a>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-secondary/50 border-t border-border">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="font-display text-3xl font-extrabold mb-8">Du kanske också vill ha</h2>
            <div className="grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {related.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            aria-label="Stäng"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightbox}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
